import React, { useState } from 'react';
import { useAppDispatch } from '../../redux/hooks';
import { setAuthView, setPlayerId, setSignupData } from '../../redux/slice/authModalSlice';
import { Images } from '../../utils/imageConstants';
import PhoneInput from 'react-phone-input-2';
import 'react-phone-input-2/lib/style.css';
import { useForm, Controller } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { signupSchema } from './validation/signupSchema';
import {
  SignUpCommand,
  type AttributeType,
} from "@aws-sdk/client-cognito-identity-provider";
import toast from "react-hot-toast";
import { Api } from '../../services/interceptor';
import type { UserAvailabilityRequest, PlayerCreationRequest, SignupFormValues, UserAvailabilityResponse } from './types/auth.d';
import { clientRegion, generateSecretHash } from '../../helper/helper';
import { useMutation } from '@tanstack/react-query';
import { QUERY_KEY } from '../../services/queryEndPoint';
import SocialAuthButtons from './SocialAuthButtons';

const Signup: React.FC = () => {
  const dispatch = useAppDispatch();
  const [showPassword, setShowPassword] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const { register, handleSubmit, formState: { errors, isValid }, setError, control } = useForm<SignupFormValues>({
    resolver: yupResolver(signupSchema),
    mode: 'onChange',
    reValidateMode: 'onChange',
    defaultValues: {
      email: '',
      mobileNumber: '',
      password: '',
      agreeToNotifications: false,
    },
  });

  //This is a backend api through which we are checking if that user already exists or not
  const checkAvailabilityMutation = useMutation<UserAvailabilityResponse, any, UserAvailabilityRequest>({
    mutationKey: [QUERY_KEY.isUserExist],
    mutationFn: async (request: UserAvailabilityRequest) => {
      const response = await Api.post(QUERY_KEY.isUserExist, request);
      console.log("Full API response:", response);
      console.log("response.data:", response?.data);
      console.log("response.data.message:", response?.data?.message);
      return response.data;
    },
  });

  const onSubmit = async (values: SignupFormValues) => {
    setIsSubmitting(true);
    try {
      // We need to separate country code and phone number
      const cleanPhone = values.mobileNumber.replace(/\D/g, ''); // Remove non-digits
      // Extract country code (first 1-3 digits depending on country)
      let countryCode = 91; // Default to India
      let phoneNumber = cleanPhone; 
      // Try to detect and extract country code
      if (cleanPhone.startsWith('91') && cleanPhone.length > 10) {
        countryCode = 91;
        phoneNumber = cleanPhone.substring(2); // Remove country code
      } else if (cleanPhone.startsWith('1') && cleanPhone.length > 10) {
        countryCode = 1;
        phoneNumber = cleanPhone.substring(1);
      } else if (cleanPhone.length > 11) {
        // Try 2-digit country code
        countryCode = parseInt(cleanPhone.substring(0, 2));
        phoneNumber = cleanPhone.substring(2);
      } else if (cleanPhone.length > 10) {
        // Try 3-digit country code (some countries like 269 for Comoros)
        countryCode = parseInt(cleanPhone.substring(0, 3));
        phoneNumber = cleanPhone.substring(3);
      }
    
      if (!countryCode || countryCode === 0) {
        countryCode = 91;
      }
      // Step 1: Check user availability
      const availabilityRequest: UserAvailabilityRequest = {
        countryCode,
        emailId: values.email,
        phoneNumber: phoneNumber,
        userName: values.email,
      };
      let availabilityResponse: any;
      try {
        availabilityResponse = await checkAvailabilityMutation.mutateAsync(availabilityRequest);
        console.log(availabilityResponse,"availabilityResponse");
        console.log("Full response structure:", JSON.stringify(availabilityResponse, null, 2));
      } catch (err: any) {
        console.error("Availability check error:", err);
        const errorMessage = err?.response?.data?.message || err?.response?.data?.error || err?.message || "Failed to check availability";
        toast.error(errorMessage);
        setIsSubmitting(false);
        return;
      }
      const responseMessage = 
        availabilityResponse?.message || 
        availabilityResponse?.data?.message || 
        (typeof availabilityResponse === 'string' ? availabilityResponse : null) ||
        availabilityResponse?.response?.message ||
        availabilityResponse?.error?.message;
      
      console.log(responseMessage,"responseMessage");
      console.log("Checking message paths:", {
        'availabilityResponse?.message': availabilityResponse?.message,
        'availabilityResponse?.data?.message': availabilityResponse?.data?.message,
        'typeof availabilityResponse': typeof availabilityResponse
      });
      
      if (responseMessage !== "Available") {
        const errorMessage = responseMessage || "User already exists";
        if (errorMessage.toLowerCase().includes("email")) {
          setError("email", { type: "manual", message: errorMessage });
        } else if (errorMessage.toLowerCase().includes("phone")) {
          setError("mobileNumber", { type: "manual", message: errorMessage });
        } else {
          setError("email", { type: "manual", message: errorMessage });
        }
        toast.error(errorMessage);
        setIsSubmitting(false);
        return; 
        //If user is not available,return from here
      }

      // Step 2: If user available, proceed with Cognito SignUp
      const secretHash = generateSecretHash(values.email);
      const cognitoPhone = `+${cleanPhone}`;
      const userAttributes: AttributeType[] = [
        {
          Name: "email",
          Value: values.email,
        },
        {
          Name: "phone_number",
          Value: cognitoPhone,
        },
      ];

      const command = new SignUpCommand({
        ClientId: import.meta.env.VITE_COGNITO_CLIENT_ID,
        Username: values.email,
        Password: values.password,
        SecretHash: secretHash,
        UserAttributes: userAttributes,
      });

      const response = await clientRegion.send(command);

      if (!response.UserSub) {
        throw new Error("Failed to create user in Cognito");
      }

      dispatch(setPlayerId(response.UserSub));

      // Step 3: Create player in backend (Tis is backend api to create user in database)
      const playerRequest: PlayerCreationRequest = {
        emailId: values.email,
        phoneNo: phoneNumber, // Phone number without country code
        userName: values.email,
        playerId: response.UserSub,
        countryCode, // Country code as separate field
      };

      try {
        await Api.post('/player', playerRequest);
      } catch (err: any) {
        console.error("Player creation error:", err);
        toast.error("Account created but failed to initialize profile");
      }
      dispatch(setSignupData({ email: values.email, userId: response.UserSub, password: values.password, mobileNumber: values.mobileNumber }));
      dispatch(setAuthView('otp'));
      toast.success("Verification code sent to your email");

    } catch (err: any) {
      console.error("Sign-up error:", err);
      const errorMessage = err?.message || "Sign-up failed";
      if (errorMessage.includes("UsernameExistsException")) {
        setError("email", { type: "manual", message: "User with this email already exists" });
        toast.error("User already exists");
      } else if (errorMessage.includes("InvalidParameterException")) {
        setError("email", { type: "manual", message: "Invalid email format" });
        toast.error("Invalid email format");
      } else if (errorMessage.includes("InvalidPasswordException")) {
        setError("password", { type: "manual", message: "Password does not meet requirements" });
        toast.error("Password does not meet requirements");
      } else {
        toast.error(errorMessage);
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const togglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };

  return (
    <div className="figma-login-container">
      <div className="login-background-image">
        <div className="login-modal-container">
          <button
            type="button"
            className="auth-screen-back"
            onClick={() => dispatch(setAuthView('signin'))}
            aria-label="Back to sign in"
          >
            <img src={Images.back_arrow} alt="" aria-hidden />
          </button>
          <div className="login-header">
            <div className="logo-container">
              <img src={Images.main_logo} alt="CRICCENTER" className="logo" />
            </div>
            <h1 className="app-name">CRICCENTER</h1>
          </div>
          <h2 className="signin-title">Sign Up</h2>
          <form onSubmit={handleSubmit(onSubmit)} className="login-form">
            <div className="form-group">
              <label className="form-label" htmlFor="signupEmail">
                Email
              </label>
              <div className="input-wrapper">
                <input 
                  id="signupEmail" 
                  className={`form-input ${errors.email ? 'error' : ''}`}
                  type="email" 
                  {...register('email')}
                  placeholder="Enter Email Address" 
                />
              </div>
              {errors.email && (
                <span className="error-message">{errors.email.message as string}</span>
              )}
            </div>
            
            <div className="form-group">
              <label className="form-label" htmlFor="signupMobile">
                Mobile Number<span className="required-asterisk">*</span>
              </label>
              <div className="input-wrapper">
                <Controller
                  name="mobileNumber"
                  control={control}
                  render={({ field }) => (
                    <PhoneInput
                      country="in"
                      value={field.value || ''}
                      onChange={field.onChange}
                      inputClass={`form-input phone-input-field ${errors.mobileNumber ? 'error' : ''}`}
                      containerClass="phone-input-container"
                      placeholder="Enter Mobile Number"
                      enableSearch
                    />
                  )}
                />
              </div>
              {errors.mobileNumber && (
                <span className="error-message">{errors.mobileNumber.message as string}</span>
              )}
            </div>
            
            <div className="form-group">
              <label className="form-label" htmlFor="signupPassword">
                Password
              </label>
              <div className="input-wrapper">
                <input 
                  id="signupPassword" 
                  className={`form-input ${errors.password ? 'error' : ''}`}
                  type={showPassword ? "text" : "password"} 
                  {...register('password')}
                  placeholder="Enter Password" 
                />
                <button 
                  type="button" 
                  className="password-toggle"
                  onClick={togglePasswordVisibility}
                  aria-label={showPassword ? "Hide password" : "Show password"}
                >
                  {showPassword ? (
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M12 4.5C7 4.5 2.73 7.61 1 12c1.73 4.39 6 7.5 11 7.5s9.27-3.11 11-7.5c-1.73-4.39-6-7.5-11-7.5zM12 17c-2.76 0-5-2.24-5-5s2.24-5 5-5 5 2.24 5 5-2.24 5-5 5zm0-8c-1.66 0-3 1.34-3 3s1.34 3 3 3 3-1.34 3-3-1.34-3-3-3z" fill="currentColor"/>
                    </svg>
                  ) : (
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M12 7c2.76 0 5 2.24 5 5 0 .65-.13 1.26-.36 1.83l2.92 2.92c1.51-1.26 2.7-2.89 3.43-4.75-1.73-4.39-6-7.5-11-7.5-1.4 0-2.74.25-3.98.7l2.16 2.16C10.74 7.13 11.35 7 12 7zM2 4.27l2.28 2.28.46.46C3.08 8.3 1.78 10.02 1 12c1.73 4.39 6 7.5 11 7.5 1.55 0 3.03-.3 4.38-.84l.42.42L19.73 22 21 20.73 3.27 3 2 4.27zM7.53 9.8l1.55 1.55c-.05.21-.08.43-.08.65 0 1.66 1.34 3 3 3 .22 0 .44-.03.65-.08l1.55 1.55c-.67.33-1.41.53-2.2.53-2.76 0-5-2.24-5-5 0-.79.2-1.53.53-2.2zm4.31-.78l3.15 3.15.02-.16c0-1.66-1.34-3-3-3l-.17.01z" fill="currentColor"/>
                    </svg>
                  )}
                </button>
              </div>
              {errors.password && (
                <span className="error-message">{errors.password.message as string}</span>
              )}
            </div>
            
            <div className="checkbox-section">
              <label className="checkbox-container">
                <input 
                  type="checkbox" 
                  {...register('agreeToNotifications')}
                />
                <span className="checkmark"></span>
                <span className="checkbox-label">
                  I agree to receive notifications from CricCenter via Email.
                </span>
              </label>
              {errors.agreeToNotifications && (
                <span className="error-message">{errors.agreeToNotifications.message as string}</span>
              )}
            </div>
            
            <div className="action-buttons">
              <button type="submit" className="btn-primary" disabled={isSubmitting || !isValid}>
                {isSubmitting ? 'Creating Account...' : 'Create Account'}
              </button>
            </div>

            <SocialAuthButtons />

            <div className="signin-link-section">
              <p className="signin-text">
                Already have an account?
                <button 
                  type="button" 
                  className="signin-link"
                  onClick={() => dispatch(setAuthView('signin'))}
                >
                  Sign In
                </button>
              </p>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default Signup;
